import { Fragment, useId, type ChangeEvent, type ReactNode } from "react";
import { css, cx } from "@strust/ui/css";
import { topbar } from "@strust/ui/recipes";
import { ChevronDownIcon, ChevronRightIcon } from "../icons/icons.tsx";
import type { SelectOption } from "../primitives/fields.tsx";

export interface TopbarProps {
  /** Accessible name of the banner's navigation (from i18n). */
  readonly label: string;
  /** Breadcrumbs or the screen's context, start side. */
  readonly start: ReactNode;
  /** Selectors and menus, end side. */
  readonly end: ReactNode;
}

/** Application banner: breadcrumbs on the start side, selectors and menus on the end side. */
export function Topbar({ label, start, end }: TopbarProps) {
  const s = topbar();
  return (
    <header className={s.root}>
      <nav className={s.start} aria-label={label}>
        {start}
      </nav>
      <div className={s.end}>{end}</div>
    </header>
  );
}

export interface Crumb {
  readonly id: string;
  /** Rendered content (a router link for all but the last crumb). */
  readonly node: ReactNode;
}

/** Breadcrumb trail; the last crumb is the current page. */
export function Breadcrumbs({ crumbs }: { readonly crumbs: readonly Crumb[] }) {
  const s = topbar();
  return (
    <ol className={s.crumbs}>
      {crumbs.map((crumb, index) => (
        <Fragment key={crumb.id}>
          {index > 0 ? (
            <li aria-hidden="true" className={s.separator}>
              <ChevronRightIcon />
            </li>
          ) : null}
          <li className={s.crumb} aria-current={index === crumbs.length - 1 ? "page" : undefined}>
            {crumb.node}
          </li>
        </Fragment>
      ))}
    </ol>
  );
}

export interface TopbarSelectProps {
  readonly label: string;
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly onChange: (value: string) => void;
  readonly icon?: ReactNode;
}

const NATIVE = css({
  appearance: "none",
  bg: "transparent",
  border: "none",
  color: "inherit",
  font: "inherit",
  pr: "5",
  cursor: "pointer",
  _focusVisible: { outline: "none" },
  "& option": { bg: "surface.2", color: "text" },
});

const CHEVRON = css({
  position: "absolute",
  insetInlineEnd: "2",
  pointerEvents: "none",
  display: "inline-flex",
  "& svg": { width: "3.5", height: "3.5" },
});

/** Native select styled as a top-bar chip (label kept for AT). */
export function TopbarSelect({
  label,
  value,
  options,
  onChange,
  icon,
}: TopbarSelectProps) {
  const id = useId();
  const s = topbar();
  const frame = cx(s.chip, css({ position: "relative", "&:focus-within": { boxShadow: "focus" } }));
  return (
    <span className={frame}>
      <label htmlFor={id} className={css({ srOnly: true })}>
        {label}
      </label>
      {icon ? (
        <span aria-hidden="true" className={css({ display: "inline-flex" })}>
          {icon}
        </span>
      ) : null}
      <select
        id={id}
        value={value}
        className={NATIVE}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => {
          onChange(event.target.value);
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span aria-hidden="true" className={CHEVRON}>
        <ChevronDownIcon />
      </span>
    </span>
  );
}

/** Read-only top-bar chip (e.g. the UTC offset). */
export function TopbarChip({
  icon,
  children,
  label,
  title,
}: {
  readonly icon?: ReactNode;
  readonly children: ReactNode;
  /** Accessible name when the visible text is an abbreviation (e.g. "Time zone America/Sao_Paulo"). */
  readonly label?: string;
  readonly title?: string;
}) {
  const s = topbar();
  return (
    <span className={s.chip} {...(label ? { role: "img", "aria-label": label } : {})} title={title}>
      {icon ? (
        <span aria-hidden="true" className={css({ display: "inline-flex" })}>
          {icon}
        </span>
      ) : null}
      <span aria-hidden={label ? true : undefined}>{children}</span>
    </span>
  );
}
